import type { Filter, FilterOption } from "~/lib/filters/types";
import type { ProductCardProductProps } from "~/components/product/ProductCard/types";
import { dineroFromFloat } from "~/lib/money";
import { toSafeInternalPath } from "~/lib/safe-url";
import type { BloomreachDoc, BloomreachFacetCounts } from "./types";

// Slider step for the price facet. Bounds are rounded to a multiple of this so the
// slider thumb can always reach them — see WEB-973 for the thrash bug this prevents.
const PRICE_STEP = 10;

// Converts a raw snake_case field name to a human-readable label.
// Names that already contain spaces (custom display names from Bloomreach) are returned as-is.
export function formatFacetLabel(name: string): string {
  if (name.includes(" ")) return name;
  if (name.includes("_")) {
    return name
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }
  return name.charAt(0).toUpperCase() + name.slice(1);
}

// Memo so unchanged products reuse the SAME card object (and Dinero) identity across
// query responses. On page-append or filter change only new/changed docs build a fresh
// card; every reused ref lets downstream Price $deriveds short-circuit (Svelte 5 skips
// reactive work when the new value === the old). Keyed on pid PLUS a signature of every
// doc field the card reads, so a product whose displayed data changed invalidates rather
// than serving a stale card.
// ponytail: plain module Map, unbounded -- a listing session is N <= 200 docs, no eviction
// needed; add an LRU only if a real leak shows up.
// Value is null for a doc whose URL failed safe-url validation — memoizes the "drop this
// product" outcome too, so an unsafe doc isn't re-validated on every subsequent render.
const cardCache = new Map<string, ProductCardProductProps | null>();

// Single source of truth for which doc fields feed a card, so the cache signature below
// can't silently drift out of sync with what buildCard() actually reads.
function pickCardFields(doc: BloomreachDoc) {
  const {
    pid,
    thumb_image,
    title,
    url,
    sale_price,
    sale_price_range,
    compare_at_price,
    has_alternative_products,
    has_alternative_sizes,
    review_rating,
    review_count,
  } = doc;
  return {
    pid,
    thumb_image,
    title,
    url,
    sale_price,
    sale_price_range,
    compare_at_price,
    has_alternative_products,
    has_alternative_sizes,
    review_rating,
    review_count,
  };
}

function cardSignature(doc: BloomreachDoc): string {
  return JSON.stringify(pickCardFields(doc));
}

// Product URLs are validated same-origin (not against the hardcoded redirect domain) —
// a product page should resolve relative to wherever the storefront is currently running
// (preview or prod), not a fixed domain. Returns null for anything unsafe or unparseable.
export function transformUrlToRelative(url: string): string | null {
  return toSafeInternalPath(url);
}

// Returns null when the doc's URL fails safe-url validation — the whole product is
// dropped from the grid rather than rendered without a link, consistent with Finding 3's
// per-product-validity philosophy (an unsafe URL is treated like any other invalid field).
function buildCard(doc: BloomreachDoc): ProductCardProductProps | null {
  const {
    pid,
    thumb_image,
    title,
    url,
    sale_price,
    sale_price_range,
    compare_at_price,
    has_alternative_products,
    has_alternative_sizes,
    review_rating,
    review_count,
  } = pickCardFields(doc);
  const safeUrl = url ? transformUrlToRelative(url) : null;
  if (url && safeUrl === null) return null;
  return {
    type: "product" as const,
    productId: pid,
    image: thumb_image,
    title,
    url: safeUrl ?? undefined,
    price: dineroFromFloat(sale_price_range?.[0] ?? sale_price),
    hasPriceRange: !!(sale_price_range && sale_price_range[0] !== sale_price_range[1]),
    compareAtPrice: compare_at_price != null ? dineroFromFloat(compare_at_price) : undefined,
    moreAvailable:
      has_alternative_products === "true" && has_alternative_sizes === "true"
        ? "colors_and_sizes"
        : has_alternative_products === "true"
          ? "colors"
          : has_alternative_sizes === "true"
            ? "sizes"
            : null,
    rating: review_rating,
    reviewCount: review_count,
  };
}

export function mapProductsToCards(docs: BloomreachDoc[]): ProductCardProductProps[] {
  const cards: ProductCardProductProps[] = [];
  for (const doc of docs) {
    const key = cardSignature(doc);
    let card = cardCache.get(key);
    if (card === undefined) {
      card = buildCard(doc);
      cardCache.set(key, card);
    }
    if (card) cards.push(card);
  }
  return cards;
}

export function mapFacetsToFilters(
  facetCounts: BloomreachFacetCounts | undefined,
  priceMax?: number,
  facetOrder?: Map<string, string[]>,
  priceMin?: number,
): Filter[] {
  if (!facetCounts) return [];

  return facetCounts.facets.flatMap((facet): Filter[] => {
    if (facet.type === "text") {
      const order = facetOrder?.get(facet.name) ?? [];
      const orderIndex = new Map(order.map((v, i) => [v, i]));
      const options = facet.value
        .filter((o) => o.count > 0)
        .map((o): FilterOption => ({ value: o.name, label: o.name, count: o.count }))
        .sort((a, b) => {
          const ia = orderIndex.get(a.value) ?? Infinity;
          const ib = orderIndex.get(b.value) ?? Infinity;
          return ia - ib;
        });
      if (options.length === 0) return [];
      return [
        { type: "checkbox" as const, id: facet.name, label: formatFacetLabel(facet.name), options },
      ];
    }
    if (facet.type === "number_range") {
      const concreteValues = facet.value
        .flatMap((b) => [b.start === "*" ? null : b.start, b.end === "*" ? null : b.end])
        .filter((n): n is number => n !== null);
      if (concreteValues.length === 0) return [];
      const isPriceFacet = facet.name.toLowerCase().includes("price");
      // Use caller-supplied priceMax/priceMin for price facets so we can show the real
      // product bounds rather than the bucket boundaries. Round to the nearest step so
      // the slider thumb can always reach them.
      const min =
        isPriceFacet && priceMin != null
          ? Math.floor(priceMin / PRICE_STEP) * PRICE_STEP
          : Math.min(...concreteValues);
      const max =
        isPriceFacet && priceMax != null
          ? Math.ceil(priceMax / PRICE_STEP) * PRICE_STEP
          : Math.max(...concreteValues);
      return [
        {
          type: "slider" as const,
          id: facet.name,
          label: formatFacetLabel(facet.name),
          min,
          max,
        },
      ];
    }
    if (facet.type === "number_stats") {
      const isPriceFacet = facet.name.toLowerCase().includes("price");
      const min =
        isPriceFacet && priceMin != null
          ? Math.floor(priceMin / PRICE_STEP) * PRICE_STEP
          : facet.value.start;
      const max =
        isPriceFacet && priceMax != null
          ? Math.ceil(priceMax / PRICE_STEP) * PRICE_STEP
          : facet.value.end;
      return [
        {
          type: "slider" as const,
          id: facet.name,
          label: formatFacetLabel(facet.name),
          min,
          max,
        },
      ];
    }
    return [];
  });
}
